import { normalizeCpfCnpj, campoEditavelCliente } from './constants';

const MOTIVOS_INCONSISTENCIA_DOCUMENTO = {
  SEM_PARTICIPANTE_ASSOCIADO: 'Documento sem participante associado',
  PARTICIPANTE_NAO_ENCONTRADO: 'Documento sem participante associado',
  PARTICIPANTE_AMBIGUO: 'Documento associado a mais de um participante',
  DOCUMENTO_IDENTIDADE_INVALIDA: 'Documento com CPF/CNPJ inválido',
  ARQUIVO_NAO_DISPONIVEL: 'Arquivo do documento não está disponível',
  ARQUIVO_AUSENTE: 'Arquivo do documento não está disponível',
  IDENTIFICADOR_DOCUMENTO_AUSENTE: 'Documento sem código para validação',
  DOCUMENTO_INCONSISTENTE: 'Documento marcado como inconsistente pela API',
};

const valorExiste = valor =>
  valor !== null && valor !== undefined && valor !== '';

// Alinhado ao CessaoTelefoneNormalizer do backend: telefone legado
// pode chegar como texto numérico com fração decimal exclusivamente zerada
// (ex.: "17982277927.000000" ou "17982277927,000000"), produzido por CAST
// direto de coluna numérica no banco. Remover essa fração ANTES de extrair
// dígitos evita acrescentar zeros artificiais ao número.
//
// Fração NÃO zerada (ex.: ".7", ".27", ".500000") não é removida por este
// regex. Isso sozinho não bastaria: um `replace(/\D/g, '')` ingênuo sobre o
// texto inteiro absorveria os dígitos da fração e, caso o total desse 10/11
// dígitos, produziria uma máscara de telefone aparentemente válida a partir
// de um valor que não é telefone. Por isso, depois da remoção da fração
// zerada, o texto restante é validado por REGEX_TELEFONE_NORMALIZAVEL antes
// de extrair dígitos: qualquer caractere fora de dígito e da formatação
// conhecida de telefone (`(`, `)`, `-`, espaço) — incluindo um separador
// decimal remanescente de fração não zerada — marca o valor como legado
// inesperado. Nesse caso é devolvido o texto original aparado, sem máscara e
// sem fabricar dígitos, a mesma decisão que o CessaoTelefoneNormalizer toma
// no backend para a mesma entrada.
const REGEX_FRACAO_DECIMAL_ZERADA = /[.,]0+$/;

// Conjunto aceito além de dígito, após a remoção da fração zerada: parênteses,
// hífen e espaço — mesma formatação conhecida aceita pelo
// CessaoTelefoneNormalizer (backend). Qualquer outro caractere torna o valor
// não normalizável.
const REGEX_TELEFONE_NORMALIZAVEL = /^[\d()\- ]*$/;

// Alinhado ao CessaoTelefoneNormalizer do backend (método
// HifenEntreDoisDigitos): dentro do conjunto acima, o hífen só é formatação
// legítima de telefone quando está ENTRE DOIS DÍGITOS (ex.: "98227-7927").
// Em qualquer outra posição — início, fim, ou adjacente a caractere que não
// seja dígito (incluindo outro hífen, parêntese ou espaço) — o hífen pode
// representar um sinal negativo ou hífen solto de dado legado corrompido, e
// não deve ser descartado silenciosamente como formatação.
const REGEX_HIFEN_FORA_DE_DOIS_DIGITOS = /(^|\D)-|-($|\D)/;

// Decisão combinada de normalizável: mesmo conjunto de caracteres de sempre,
// mais a restrição de posição do hífen acima. Mantida como função separada
// para que a verificação de normalizável continue acontecendo antes e de
// forma independente da extração de dígitos.
const ehTelefoneNormalizavel = texto =>
  REGEX_TELEFONE_NORMALIZAVEL.test(texto) &&
  !REGEX_HIFEN_FORA_DE_DOIS_DIGITOS.test(texto);

const removerFracaoDecimalZeradaTelefone = texto =>
  REGEX_FRACAO_DECIMAL_ZERADA.test(texto)
    ? texto.replace(REGEX_FRACAO_DECIMAL_ZERADA, '')
    : texto;

const mascararDigitosTelefone = digitos => {
  if (digitos.length === 11) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(
      7
    )}`;
  }
  if (digitos.length === 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(
      6
    )}`;
  }
  // Fallback explícito: quantidade inesperada de dígitos. Devolve a sequência
  // limpa, sem truncar e sem aplicar máscara de fixo/celular, para evidenciar
  // o dado legado em vez de inventar um número aparentemente válido.
  return digitos;
};

// Formatação defensiva do Telefone específica do contexto da cessão. Não
// reutiliza nem altera o formatador genérico compartilhado de Helpers.js:
// aquele não remove fração decimal zerada nem garante fallback sem truncar
// para quantidades de dígitos fora de 10/11.
export const formatarTelefoneCessao = valorBruto => {
  if (!valorExiste(valorBruto)) return '';

  const textoOriginal = String(valorBruto).trim();
  if (!textoOriginal) return '';

  const textoSemFracaoZerada = removerFracaoDecimalZeradaTelefone(
    textoOriginal
  );

  // Valor legado inesperado (fração decimal não zerada,
  // letras, hífen fora de posição de formatação legítima — ver
  // REGEX_HIFEN_FORA_DE_DOIS_DIGITOS — ou qualquer outro caractere fora da
  // formatação conhecida de telefone). Fallback explícito: devolve o texto
  // original aparado, sem máscara e sem fabricar dígitos com o que sobrou da
  // fração.
  if (!ehTelefoneNormalizavel(textoSemFracaoZerada)) {
    return textoOriginal;
  }

  const digitos = textoSemFracaoZerada.replace(/\D/g, '');

  if (!digitos) return '';

  return mascararDigitosTelefone(digitos);
};

export const obterIdentificadorEstavelParticipante = pessoa => {
  if (!pessoa || !valorExiste(pessoa.identificador_participante)) return '';
  return String(pessoa.identificador_participante);
};

export const obterChaveParticipante = (pessoa, tipo) => {
  const identificador = obterIdentificadorEstavelParticipante(pessoa);
  return identificador ? `${tipo}_${identificador}` : '';
};

// Chave exclusiva para a árvore React. Não reutilizar em estado, payload ou
// conferência: sem identificador_participante não existe identidade segura.
export const obterChaveRenderizacaoParticipante = (pessoa, tipo, indice) => {
  const chaveParticipante = obterChaveParticipante(pessoa, tipo);
  return chaveParticipante || `${tipo}_render_only_${indice}`;
};

export const obterChaveCampoParticipante = (pessoa, tipo, sufixo) => {
  const chaveParticipante = obterChaveParticipante(pessoa, tipo);
  return chaveParticipante ? `${chaveParticipante}${sufixo}` : '';
};

export const obterIdentificadorDocumento = documento => {
  if (!documento) return '';
  if (valorExiste(documento.codigo)) return String(documento.codigo);
  return '';
};

export const obterChaveRenderizacaoDocumento = (documento, indice) => {
  const identificador = obterIdentificadorDocumento(documento);
  if (identificador) return `documento-${identificador}-${indice}`;
  return `documento-sem-identificador-${indice}`;
};

export const obterChavesPessoa = (pessoa, tipo) => {
  if (!pessoa || !obterChaveParticipante(pessoa, tipo)) return [];

  const chaveParticipante = obterChaveParticipante(pessoa, tipo);
  const chaves = [`${chaveParticipante}_nome`, `${chaveParticipante}_cpf`];
  if (pessoa.tipo_pessoa !== '1') {
    chaves.push(
      `${chaveParticipante}_rg`,
      `${chaveParticipante}_estado_civil`,
      `${chaveParticipante}_data_nascimento`,
      `${chaveParticipante}_profissao`,
      `${chaveParticipante}_nacionalidade`
    );
  }
  chaves.push(
    `${chaveParticipante}_email`,
    `${chaveParticipante}_telefone`,
    `${chaveParticipante}_endereco`
  );
  if (pessoa.nome_conjuge) chaves.push(`${chaveParticipante}_conjuge`);
  return chaves;
};

const obterPessoasDetalhes = detalhes => [
  ...((detalhes && detalhes.cedentes) || []),
  ...((detalhes && detalhes.cessionarios) || []),
];

export const obterParticipantesSemIdentificadorEstavel = detalhes =>
  obterPessoasDetalhes(detalhes).filter(
    pessoa => !obterIdentificadorEstavelParticipante(pessoa)
  );

const obterPessoaPorIdentificadorEstavel = (pessoas, identificador) => {
  if (!identificador) return null;
  const correspondencias = pessoas.filter(
    pessoa =>
      obterIdentificadorEstavelParticipante(pessoa) === String(identificador)
  );
  return correspondencias.length === 1 ? correspondencias[0] : null;
};

export const obterPessoaDaConferencia = (pessoas, campo) => {
  if (!Array.isArray(pessoas) || !campo) return null;

  if (!valorExiste(campo.identificador_participante)) return null;
  return obterPessoaPorIdentificadorEstavel(
    pessoas,
    campo.identificador_participante
  );
};

// Códigos de erro seguro para quando um campo de pessoa (CEDENTE/CESSIONARIO)
// não pode ser associado com segurança a um participante. Nunca acompanhados
// de CPF, nome ou e-mail.
export const ERRO_IDENTIDADE_PARTICIPANTE = {
  IDENTIFICADOR_AUSENTE: 'identificador_participante_ausente',
  PARTICIPANTE_NAO_ENCONTRADO: 'participante_nao_encontrado',
  PARTICIPANTE_DUPLICADO: 'participante_duplicado',
  // Um documento sem código não pode virar item de conferência (não há como
  // validar nem persistir), mas também não pode ser descartado em silêncio —
  // precisa aparecer como erro explícito, assim como a pessoa sem identidade.
  DOCUMENTO_SEM_CODIGO: 'documento_sem_codigo',
};

const MENSAGENS_ERRO_IDENTIDADE_PARTICIPANTE = {
  [ERRO_IDENTIDADE_PARTICIPANTE.IDENTIFICADOR_AUSENTE]:
    'Não foi possível confirmar a identidade de um dos participantes da conferência. Recarregue a solicitação e tente novamente. Se o problema persistir, acione o suporte.',
  [ERRO_IDENTIDADE_PARTICIPANTE.PARTICIPANTE_NAO_ENCONTRADO]:
    'Um dos participantes da conferência não foi localizado na solicitação. Recarregue a solicitação e tente novamente. Se o problema persistir, acione o suporte.',
  [ERRO_IDENTIDADE_PARTICIPANTE.PARTICIPANTE_DUPLICADO]:
    'Há mais de um participante com o mesmo identificador nesta solicitação. Acione o suporte antes de continuar.',
  [ERRO_IDENTIDADE_PARTICIPANTE.DOCUMENTO_SEM_CODIGO]:
    'Um dos documentos da conferência está sem código de identificação. Recarregue a solicitação e tente novamente. Se o problema persistir, acione o suporte.',
};

export const obterMensagemErroIdentidadeParticipante = erro =>
  MENSAGENS_ERRO_IDENTIDADE_PARTICIPANTE[erro] ||
  'Não foi possível confirmar a identidade de um dos participantes da conferência. Recarregue a solicitação e tente novamente.';

// Função pura que resolve o item de payload de um campo de pessoa a partir do
// identificador já conhecido pelo próprio objeto da pessoa exibida na tela —
// nunca por CPF, nome, índice ou chave de renderização (`_render_only_`).
// `participantesDoPapel` é a mesma lista
// (cedentes ou cessionarios) de onde `pessoa` foi obtida; ela só é usada para
// comprovar que o identificador pertence a exatamente uma pessoa daquele
// papel (defesa contra ambiguidade), nunca para localizar a pessoa por outro
// critério.
export const resolverItemConferenciaParticipante = ({
  pessoa,
  participantesDoPapel,
  tipoRegistro,
  nomeCampo,
  valido,
  editavelCliente,
}) => {
  const identificador = obterIdentificadorEstavelParticipante(pessoa);
  if (!identificador) {
    return {
      sucesso: false,
      erro: ERRO_IDENTIDADE_PARTICIPANTE.IDENTIFICADOR_AUSENTE,
    };
  }

  const lista = Array.isArray(participantesDoPapel) ? participantesDoPapel : [];
  const correspondencias = lista.filter(
    candidato =>
      obterIdentificadorEstavelParticipante(candidato) === identificador
  );

  if (correspondencias.length === 0) {
    return {
      sucesso: false,
      erro: ERRO_IDENTIDADE_PARTICIPANTE.PARTICIPANTE_NAO_ENCONTRADO,
    };
  }
  if (correspondencias.length > 1) {
    return {
      sucesso: false,
      erro: ERRO_IDENTIDADE_PARTICIPANTE.PARTICIPANTE_DUPLICADO,
    };
  }

  return {
    sucesso: true,
    item: {
      tipo_registro: tipoRegistro,
      documento: normalizeCpfCnpj(pessoa.cpf),
      codigo_documento: null,
      nome_campo: nomeCampo,
      valido,
      editavel_cliente: editavelCliente,
      identificador_participante: identificador,
    },
  };
};

const adicionarAssociacaoDocumento = (
  associacoesPorDocumento,
  identificadorDocumento,
  associacao
) => {
  const associacoesAtuais =
    associacoesPorDocumento.get(identificadorDocumento) || [];
  const chaveAssociacao = associacao.identificador_participante
    ? `${associacao.tipo}_${associacao.identificador_participante}`
    : associacao.tipo;
  if (!associacoesAtuais.some(item => item.chave === chaveAssociacao)) {
    associacoesAtuais.push({ ...associacao, chave: chaveAssociacao });
  }
  associacoesPorDocumento.set(identificadorDocumento, associacoesAtuais);
};

const adicionarDocumento = (
  documentosPorIdentificador,
  documentosSemIdentificador,
  associacoesPorDocumento,
  documento,
  associacao
) => {
  const identificadorDocumento = obterIdentificadorDocumento(documento);
  if (!identificadorDocumento) {
    documentosSemIdentificador.push({ documento, associacao });
    return;
  }

  const documentoExistente = documentosPorIdentificador.get(
    identificadorDocumento
  );
  documentosPorIdentificador.set(
    identificadorDocumento,
    documentoExistente ? { ...documento, ...documentoExistente } : documento
  );

  if (associacao) {
    adicionarAssociacaoDocumento(
      associacoesPorDocumento,
      identificadorDocumento,
      associacao
    );
  }
};

const criarInconsistenciaDocumento = (
  documento,
  identificadorDocumento,
  motivos,
  associacoes
) => ({
  documento,
  identificador_documento: identificadorDocumento,
  motivos,
  associacoes,
});

const ehAssociacaoLote = associacao => associacao.tipo === 'Lote / Imóvel';

// Identidade só é considerada conhecida quando já vem pronta: no próprio
// documento ou por exatamente UMA associação pessoal. Documento associado
// exclusivamente ao lote/imóvel nunca recebe identidade de participante,
// mesmo que o objeto do documento traga um valor residual — a checagem do
// lote vem antes de olhar o próprio documento por isso. Nunca deriva de CPF,
// nome, índice ou posição.
const obterIdentificadorParticipanteConhecidoDocumento = (
  documento,
  associacoesDoDocumento
) => {
  const somenteAssociacoesDeLote =
    associacoesDoDocumento.length > 0 &&
    associacoesDoDocumento.every(ehAssociacaoLote);
  if (somenteAssociacoesDeLote) {
    return null;
  }

  if (valorExiste(documento.identificador_participante)) {
    return String(documento.identificador_participante);
  }
  if (
    associacoesDoDocumento.length === 1 &&
    valorExiste(associacoesDoDocumento[0].identificador_participante)
  ) {
    return String(associacoesDoDocumento[0].identificador_participante);
  }
  return null;
};

export const construirAnaliseDocumentos = detalhes => {
  const documentosPorIdentificador = new Map();
  const documentosSemIdentificador = [];
  const associacoesPorDocumento = new Map();

  ((detalhes && detalhes.documentos) || []).forEach(documento => {
    adicionarDocumento(
      documentosPorIdentificador,
      documentosSemIdentificador,
      associacoesPorDocumento,
      documento,
      null
    );
  });

  const adicionarDocumentosDaPessoa = (pessoa, tipo) => {
    (pessoa.documentos || []).forEach(documento => {
      adicionarDocumento(
        documentosPorIdentificador,
        documentosSemIdentificador,
        associacoesPorDocumento,
        documento,
        {
          tipo,
          nome: pessoa.nome,
          identificador_participante: obterIdentificadorEstavelParticipante(
            pessoa
          ),
        }
      );
    });
  };

  (detalhes && detalhes.cedentes ? detalhes.cedentes : []).forEach(pessoa =>
    adicionarDocumentosDaPessoa(pessoa, 'Cedente')
  );
  (detalhes && detalhes.cessionarios
    ? detalhes.cessionarios
    : []
  ).forEach(pessoa => adicionarDocumentosDaPessoa(pessoa, 'Cessionário'));

  ((detalhes && detalhes.lote && detalhes.lote.documentos) || []).forEach(
    documento => {
      adicionarDocumento(
        documentosPorIdentificador,
        documentosSemIdentificador,
        associacoesPorDocumento,
        documento,
        { tipo: 'Lote / Imóvel', nome: 'Lote / Imóvel' }
      );
    }
  );

  const documentos = Array.from(documentosPorIdentificador.entries()).map(
    ([identificadorDocumento, documento]) => {
      const associacoesDoDocumento =
        associacoesPorDocumento.get(identificadorDocumento) || [];
      const identificadorParticipante = obterIdentificadorParticipanteConhecidoDocumento(
        documento,
        associacoesDoDocumento
      );
      // Remove um eventual identificador_participante já presente no objeto
      // bruto: o valor final é sempre o recalculado acima, nunca o residual.
      const { identificador_participante: _semUso, ...documentoBase } = documento;

      return {
        ...documentoBase,
        identificador_documento: identificadorDocumento,
        // Preserva a identidade pessoal já conhecida; documento de lote
        // nunca recebe identidade de participante.
        ...(identificadorParticipante
          ? { identificador_participante: identificadorParticipante }
          : {}),
      };
    }
  );

  const documentosInconsistentes = documentos.reduce(
    (inconsistencias, documento) => {
      const associacoes =
        associacoesPorDocumento.get(documento.identificador_documento) || [];
      const motivos = [];
      if (documento.inconsistente) {
        motivos.push(
          valorExiste(documento.codigo_inconsistencia)
            ? documento.codigo_inconsistencia
            : 'DOCUMENTO_INCONSISTENTE'
        );
      }
      if (
        associacoes.length === 0 &&
        !motivos.includes('SEM_PARTICIPANTE_ASSOCIADO') &&
        !motivos.includes('PARTICIPANTE_NAO_ENCONTRADO')
      ) {
        motivos.push('SEM_PARTICIPANTE_ASSOCIADO');
      }
      if (!documento.inconsistente && associacoes.length > 1) {
        motivos.push('PARTICIPANTE_AMBIGUO');
      }
      const arquivoExplicitamenteIndisponivel =
        documento.arquivo_disponivel === false ||
        (documento.arquivo_disponivel === undefined &&
          Object.prototype.hasOwnProperty.call(documento, 'caminho_arquivo') &&
          !documento.caminho_arquivo);
      if (arquivoExplicitamenteIndisponivel && !motivos.includes('ARQUIVO_AUSENTE')) {
        motivos.push('ARQUIVO_NAO_DISPONIVEL');
      }
      if (motivos.length > 0) {
        inconsistencias.push(
          criarInconsistenciaDocumento(
            documento,
            documento.identificador_documento,
            motivos,
            associacoes
          )
        );
      }
      return inconsistencias;
    },
    []
  );

  documentosSemIdentificador.forEach(({ documento, associacao }) => {
    documentos.push({ ...documento, identificador_documento: '' });
    documentosInconsistentes.push(
      criarInconsistenciaDocumento(
        documento,
        '',
        ['IDENTIFICADOR_DOCUMENTO_AUSENTE'],
        associacao ? [associacao] : []
      )
    );
  });

  return { documentos, documentosInconsistentes };
};

// Catálogo único dos campos de pessoa considerados na conferência. Não é uma
// "lista de bloqueio" — apenas enumera quais campos existem por tipo de
// pessoa; quem decide se o campo é editável pelo cliente continua sendo
// exclusivamente `campoEditavelCliente`/o valor canônico da WebAPI.
const CAMPOS_PESSOA_IDENTIFICACAO = [
  { sufixo: '_nome', nome: 'Nome' },
  { sufixo: '_cpf', nome: 'CPF' },
];

const CAMPOS_PESSOA_DOCUMENTAIS_PF = [
  { sufixo: '_rg', nome: 'RG' },
  { sufixo: '_estado_civil', nome: 'EstadoCivil' },
  { sufixo: '_data_nascimento', nome: 'DataNascimento' },
  { sufixo: '_profissao', nome: 'Profissao' },
  { sufixo: '_nacionalidade', nome: 'Nacionalidade' },
];

const CAMPOS_PESSOA_CONTATO = [
  { sufixo: '_email', nome: 'Email' },
  { sufixo: '_telefone', nome: 'Telefone' },
  { sufixo: '_endereco', nome: 'Endereco' },
];

const obterCamposConferenciaPessoa = pessoa => {
  const campos = [...CAMPOS_PESSOA_IDENTIFICACAO];
  if (pessoa.tipo_pessoa !== '1') {
    campos.push(...CAMPOS_PESSOA_DOCUMENTAIS_PF);
  }
  campos.push(...CAMPOS_PESSOA_CONTATO);
  if (pessoa.nome_conjuge) {
    campos.push({ sufixo: '_conjuge', nome: 'NomeConjuge' });
  }
  return campos;
};

const CAMPOS_CONFERENCIA_LOTE = [
  { chave: 'lote_empreendimento', nome: 'Empreendimento' },
  { chave: 'lote_quadra', nome: 'Quadra' },
  { chave: 'lote_lote', nome: 'Lote' },
  { chave: 'lote_area_total', nome: 'AreaTotal' },
  { chave: 'lote_cidade', nome: 'Cidade' },
  { chave: 'lote_tipo_cessao', nome: 'TipoCessao' },
];

const CAMPOS_CONFERENCIA_SOLICITACAO = [
  { chave: 'solicitacao_ligacao_agua', nome: 'Possui_Ligacao_Agua' },
  { chave: 'solicitacao_construcao', nome: 'Possui_Construcao' },
];

// Um campo só é efetivamente reprovado quando está conferido E marcado
// inválido ao mesmo tempo. Se o especialista desmarcar a conferência, a marca
// de inválido pode continuar residualmente no estado local — mas, sem a
// conferência ativa, o campo volta a ser somente pendente.
const obterEstadoLocalCampo = (chave, conferencias, camposInvalidos) => {
  const foiConferido = Boolean(chave) && conferencias[chave] === true;
  if (!foiConferido) return null;
  return camposInvalidos[chave] === true ? false : true;
};

// Ao reabrir a conferência, a WebAPI já devolveu a editabilidade canônica de
// cada campo persistido. Essa classificação prevalece. Só quando o campo
// ainda não foi persistido (item novo, nunca salvo) o catálogo local
// `campoEditavelCliente` é usado — e apenas ele, sem outra heurística.
const obterEditavelClienteCampo = (
  chave,
  nomeCampo,
  tipoRegistro,
  editavelClientePersistido
) => {
  if (
    chave &&
    editavelClientePersistido &&
    Object.prototype.hasOwnProperty.call(editavelClientePersistido, chave)
  ) {
    return editavelClientePersistido[chave];
  }
  return campoEditavelCliente(nomeCampo, tipoRegistro);
};

const classificarEstadoItem = item => {
  if (item.valido === null || item.valido === undefined) return 'pendente';
  if (item.valido === true) return 'aprovado';
  return item.editavel_cliente ? 'reprovado_editavel' : 'reprovado_nao_editavel';
};

// Classificação única e pura da conferência. Cada campo — pessoa, lote,
// solicitação ou documento — termina em exatamente um dos quatro estados
// abaixo. Payload, contadores e alertas do CRM devem ser projeções desta
// mesma coleção; nenhum deles pode recalcular a regra em paralelo.
//
// Identidade ausente/ambígua de pessoa e documento sem código de
// identificação são falhas de segurança, não estados de campo: nesse caso a
// função devolve a coleção vazia (nunca um payload parcial) e relata o(s)
// motivo(s) em `errosIdentidade`.
export const classificarConferenciaCessao = ({
  detalhes,
  conferencias = {},
  camposInvalidos = {},
  documentosValidacao = {},
  editavelClientePersistido = {},
} = {}) => {
  const itens = [];
  const errosIdentidade = [];

  const processarPessoa = (pessoa, tipo, participantesDoPapel) => {
    if (!pessoa) return;
    const tipoRegistro = tipo === 'cedente' ? 'CEDENTE' : 'CESSIONARIO';

    obterCamposConferenciaPessoa(pessoa).forEach(({ sufixo, nome }) => {
      const chave = obterChaveCampoParticipante(pessoa, tipo, sufixo);
      const valido = obterEstadoLocalCampo(chave, conferencias, camposInvalidos);
      const editavelCliente = obterEditavelClienteCampo(
        chave,
        nome,
        tipoRegistro,
        editavelClientePersistido
      );

      const resultado = resolverItemConferenciaParticipante({
        pessoa,
        participantesDoPapel,
        tipoRegistro,
        nomeCampo: nome,
        valido,
        editavelCliente,
      });

      if (!resultado.sucesso) {
        errosIdentidade.push(resultado.erro);
        return;
      }

      itens.push({ ...resultado.item, chave });
    });
  };

  (detalhes && detalhes.cedentes ? detalhes.cedentes : []).forEach(pessoa =>
    processarPessoa(pessoa, 'cedente', detalhes ? detalhes.cedentes : [])
  );
  (detalhes && detalhes.cessionarios ? detalhes.cessionarios : []).forEach(
    pessoa =>
      processarPessoa(
        pessoa,
        'cessionario',
        detalhes ? detalhes.cessionarios : []
      )
  );

  CAMPOS_CONFERENCIA_LOTE.forEach(({ chave, nome }) => {
    itens.push({
      chave,
      tipo_registro: 'LOTE',
      documento: null,
      codigo_documento: null,
      nome_campo: nome,
      valido: obterEstadoLocalCampo(chave, conferencias, camposInvalidos),
      editavel_cliente: obterEditavelClienteCampo(
        chave,
        nome,
        'LOTE',
        editavelClientePersistido
      ),
    });
  });

  CAMPOS_CONFERENCIA_SOLICITACAO.forEach(({ chave, nome }) => {
    itens.push({
      chave,
      tipo_registro: 'SOLICITACAO',
      documento: null,
      codigo_documento: null,
      nome_campo: nome,
      valido: obterEstadoLocalCampo(chave, conferencias, camposInvalidos),
      editavel_cliente: obterEditavelClienteCampo(
        chave,
        nome,
        'SOLICITACAO',
        editavelClientePersistido
      ),
    });
  });

  if (detalhes) {
    const analise = construirAnaliseDocumentos(detalhes);
    const documentosInconsistentes = new Set(
      analise.documentosInconsistentes
        .map(item => item.identificador_documento)
        .filter(Boolean)
    );

    analise.documentos.forEach(doc => {
      const identificadorDocumento = obterIdentificadorDocumento(doc);
      if (!identificadorDocumento) {
        errosIdentidade.push(ERRO_IDENTIDADE_PARTICIPANTE.DOCUMENTO_SEM_CODIGO);
        return;
      }
      // Documento com código, mas já sinalizado inconsistente (sem
      // participante associado, associação ambígua etc.) segue tratado pela
      // tela de inconsistências documentais; isso é fluxo documental, fora do
      // escopo desta classificação.
      if (documentosInconsistentes.has(identificadorDocumento)) return;

      const validacaoLocal = documentosValidacao[identificadorDocumento];
      const docValido = validacaoLocal !== undefined ? validacaoLocal : null;
      const nomeCampoDocumento = doc.tipo_documento_descricao || 'Documento';
      const chaveDocumento = `documento_${identificadorDocumento}`;

      itens.push({
        chave: chaveDocumento,
        tipo_registro: 'DOCUMENTO',
        documento: doc.cpf ? normalizeCpfCnpj(doc.cpf) : null,
        codigo_documento: identificadorDocumento,
        nome_campo: nomeCampoDocumento,
        valido: docValido,
        editavel_cliente: obterEditavelClienteCampo(
          chaveDocumento,
          nomeCampoDocumento,
          'DOCUMENTO',
          editavelClientePersistido
        ),
        ...(doc.identificador_participante
          ? { identificador_participante: doc.identificador_participante }
          : {}),
      });
    });
  }

  if (errosIdentidade.length > 0) {
    return {
      itens: [],
      payload: [],
      pendentes: [],
      aprovados: [],
      reprovadosEditaveis: [],
      reprovadosNaoEditaveis: [],
      errosIdentidade,
    };
  }

  const itensClassificados = itens.map(item => ({
    ...item,
    estado: classificarEstadoItem(item),
  }));

  const payload = itensClassificados.map(({ chave: _chave, estado: _estado, ...itemPayload }) => itemPayload);

  return {
    itens: itensClassificados,
    payload,
    pendentes: itensClassificados.filter(item => item.estado === 'pendente'),
    aprovados: itensClassificados.filter(item => item.estado === 'aprovado'),
    reprovadosEditaveis: itensClassificados.filter(
      item => item.estado === 'reprovado_editavel'
    ),
    reprovadosNaoEditaveis: itensClassificados.filter(
      item => item.estado === 'reprovado_nao_editavel'
    ),
    errosIdentidade: [],
  };
};

export const calcularTotalDocumentosValidados = (
  documentos,
  documentosValidacao
) =>
  documentos.filter(documento => {
    const identificador = obterIdentificadorDocumento(documento);
    return identificador && documentosValidacao[identificador] !== undefined;
  }).length;

export const todosDocumentosValidados = (documentos, documentosValidacao) =>
  documentos.every(documento => {
    const identificador = obterIdentificadorDocumento(documento);
    return (
      Boolean(identificador) && documentosValidacao[identificador] !== undefined
    );
  });

export const todosDocumentosAprovados = (documentos, documentosValidacao) =>
  documentos.every(documento => {
    const identificador = obterIdentificadorDocumento(documento);
    return (
      Boolean(identificador) && documentosValidacao[identificador] === true
    );
  });

const normalizarCodigoDocumento = codigo =>
  valorExiste(codigo) ? String(codigo).trim() : '';

const obterValidacaoLocal = (documentosValidacao, codigoDocumento) => {
  if (
    !documentosValidacao ||
    typeof documentosValidacao !== 'object' ||
    Array.isArray(documentosValidacao)
  ) {
    return { encontrada: false, valor: undefined };
  }

  const chave = Object.keys(documentosValidacao).find(
    item => normalizarCodigoDocumento(item) === codigoDocumento
  );
  if (chave === undefined) return { encontrada: false, valor: undefined };

  return { encontrada: true, valor: documentosValidacao[chave] };
};

const criarItemAguardandoReenvio = conferencia => ({
  codigoConferencia: valorExiste(conferencia.codigo)
    ? conferencia.codigo
    : null,
  codigoDocumento: valorExiste(conferencia.codigo_documento)
    ? conferencia.codigo_documento
    : null,
  nomeCampo: valorExiste(conferencia.nome_campo)
    ? conferencia.nome_campo
    : null,
  descricaoCampo: valorExiste(conferencia.descricao_campo)
    ? conferencia.descricao_campo
    : null,
  documento: valorExiste(conferencia.documento)
    ? conferencia.documento
    : null,
  identificadorParticipante: valorExiste(
    conferencia.identificador_participante
  )
    ? conferencia.identificador_participante
    : null,
});

const criarInconsistenciaConferencia = (codigo, conferencia) => ({
  codigo,
  conferencia: criarItemAguardandoReenvio(conferencia),
});

export const construirResumoDocumental = ({
  documentos,
  documentosValidacao,
  conferenciasDocumentais,
} = {}) => {
  const documentosAtuais = Array.isArray(documentos) ? documentos : [];
  const conferencias = Array.isArray(conferenciasDocumentais)
    ? conferenciasDocumentais.filter(
        conferencia =>
          conferencia &&
          String(conferencia.tipo_registro || '')
            .trim()
            .toUpperCase() === 'DOCUMENTO'
      )
    : [];
  const inconsistencias = [];
  const codigosDocumentosAtuais = new Set();

  let aprovados = 0;
  let reprovadosAtuais = 0;
  let pendentes = 0;

  documentosAtuais.forEach(documento => {
    const codigoDocumento = normalizarCodigoDocumento(
      obterIdentificadorDocumento(documento)
    );

    if (!codigoDocumento) {
      pendentes += 1;
      inconsistencias.push({
        codigo: 'DOCUMENTO_ATUAL_SEM_CODIGO',
        documento,
      });
      return;
    }

    codigosDocumentosAtuais.add(codigoDocumento);
    const validacaoLocal = obterValidacaoLocal(
      documentosValidacao,
      codigoDocumento
    );
    const conferenciaCorrespondente = conferencias.find(
      conferencia =>
        normalizarCodigoDocumento(conferencia.codigo_documento) ===
        codigoDocumento
    );
    const validacao = validacaoLocal.encontrada
      ? validacaoLocal.valor
      : conferenciaCorrespondente && conferenciaCorrespondente.valido;

    if (validacao === true) {
      aprovados += 1;
    } else if (validacao === false) {
      reprovadosAtuais += 1;
    } else {
      pendentes += 1;
    }
  });

  const itensAguardandoReenvio = [];
  conferencias.forEach(conferencia => {
    const codigoDocumento = normalizarCodigoDocumento(
      conferencia.codigo_documento
    );

    if (!codigoDocumento) {
      inconsistencias.push(
        criarInconsistenciaConferencia(
          'CONFERENCIA_DOCUMENTAL_SEM_CODIGO',
          conferencia
        )
      );
      if (conferencia.valido === false) {
        itensAguardandoReenvio.push(
          criarItemAguardandoReenvio(conferencia)
        );
      }
      return;
    }

    if (codigosDocumentosAtuais.has(codigoDocumento)) return;

    if (conferencia.valido === false) {
      itensAguardandoReenvio.push(criarItemAguardandoReenvio(conferencia));
      return;
    }

    inconsistencias.push(
      criarInconsistenciaConferencia(
        conferencia.valido === true
          ? 'CONFERENCIA_APROVADA_SEM_DOCUMENTO'
          : 'CONFERENCIA_PENDENTE_SEM_DOCUMENTO',
        conferencia
      )
    );
  });

  const totalAtuais = documentosAtuais.length;
  const reprovadosAguardandoReenvio = itensAguardandoReenvio.length;
  const totalLogico = totalAtuais + reprovadosAguardandoReenvio;
  const todosConferidos =
    totalAtuais > 0 &&
    pendentes === 0 &&
    reprovadosAguardandoReenvio === 0 &&
    inconsistencias.length === 0;
  const todosAprovados =
    todosConferidos &&
    aprovados === totalAtuais &&
    reprovadosAtuais === 0;

  return {
    totalAtuais,
    aprovados,
    reprovadosAtuais,
    pendentes,
    reprovadosAguardandoReenvio,
    totalLogico,
    todosConferidos,
    todosAprovados,
    temReprovacao:
      reprovadosAtuais > 0 || reprovadosAguardandoReenvio > 0,
    inconsistencias,
    itensAguardandoReenvio,
  };
};

// Mensagem funcional principal para qualquer conferência incompleta. Única
// para aprovação e reprovação — não existe mais uma versão exclusiva de
// aprovação.
export const MENSAGEM_DECISAO_INCOMPLETA =
  'Analise todos os campos e anexos antes de concluir a decisão.';

// Toda decisão final (aprovação = 2, reprovação = 3) exige a mesma cobertura:
// zero pendências na classificação íntegra da conferência (`itens`/`pendentes`
// vêm de `classificarConferenciaCessao`, a única fonte usada para decidir).
// A diferença entre as duas decisões fica só onde a regra de validade
// realmente difere: aprovação não aceita nenhum item reprovado, editável ou
// não; reprovação aceita qualquer mistura de aprovados e reprovados,
// inclusive todos aprovados. Não há mais flag de configuração capaz de
// dispensar essa cobertura para a reprovação.
export const obterBloqueiosDecisao = ({
  decisao,
  classificacao,
  documentosInconsistentes = [],
}) => {
  if (!decisao) return [];

  const bloqueios = [];
  const errosIdentidade = (classificacao && classificacao.errosIdentidade) || [];
  const pendentes = (classificacao && classificacao.pendentes) || [];
  const reprovadosEditaveis =
    (classificacao && classificacao.reprovadosEditaveis) || [];
  const reprovadosNaoEditaveis =
    (classificacao && classificacao.reprovadosNaoEditaveis) || [];

  if (errosIdentidade.length > 0) {
    bloqueios.push(
      'Não é possível concluir a decisão: há inconsistência de identidade na conferência. Acione o suporte.'
    );
  }

  if (documentosInconsistentes.length > 0) {
    bloqueios.push(
      'Existem documentos inconsistentes. Corrija a associação ou acione o suporte antes da decisão.'
    );
  }

  if (pendentes.length > 0) {
    const pendentesCampos = pendentes.filter(
      item => item.tipo_registro !== 'DOCUMENTO'
    ).length;
    const pendentesAnexos = pendentes.filter(
      item => item.tipo_registro === 'DOCUMENTO'
    ).length;
    bloqueios.push(MENSAGEM_DECISAO_INCOMPLETA);
    bloqueios.push(
      `Campos pendentes: ${pendentesCampos}. Anexos pendentes: ${pendentesAnexos}.`
    );
  }

  if (
    decisao === 2 &&
    reprovadosEditaveis.length + reprovadosNaoEditaveis.length > 0
  ) {
    bloqueios.push(
      'Todos os campos e anexos precisam estar aprovados para concluir a aprovação.'
    );
  }

  return bloqueios;
};

export const obterDescricaoInconsistenciaDocumento = motivo =>
  MOTIVOS_INCONSISTENCIA_DOCUMENTO[motivo] ||
  'Documento com inconsistência não identificada';

export const obterMensagemFalhaDownload = documento => {
  const identificador = obterIdentificadorDocumento(documento);
  return `Download do documento${
    identificador ? ` ${identificador}` : ''
  } foi negado ou está indisponível. Solicite a liberação ao suporte.`;
};
